// Canonical Zod contract for the /api/me/* surface between the control plane
// (provider) and the EchoWork desktop (consumer). Single source of truth: the
// fork imports this file directly (relative path, see DIV-183), the control
// plane imports it via the @echo-work-platform/shared-types package. Both
// sides run zod@4.
import { z } from 'zod';
// ─── GET /api/me ────────────────────────────────────────
export const MeSegmentSchema = z
    .object({
    segmentId: z.string(),
    segmentName: z.string(),
    source: z.enum(['IDP_GROUP', 'DEFAULT_ALL']),
})
    .catchall(z.unknown());
export const MePrivacyNoticeSchema = z
    .object({
    required: z.boolean(),
    noticeId: z.string().nullable(),
    version: z.number().nullable(),
    filename: z.string().nullable(),
    sizeBytes: z.number().nullable(),
    publishedAt: z.string().nullable(),
    acceptedAt: z.string().nullable(),
})
    .catchall(z.unknown());
export const MeResponseSchema = z
    .object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    groups: z.array(z.string()),
    roles: z.array(z.string()),
    segments: z.array(MeSegmentSchema),
    privacyNotice: MePrivacyNoticeSchema,
})
    .catchall(z.unknown());
// ─── GET /api/me/quota ──────────────────────────────────
export const QuotaScopeSchema = z.enum(['ORG', 'SEGMENT', 'USER']);
// One applicable limit for a user, with its own (possibly shared) spend pool.
export const QuotaLimitSchema = z
    .object({
    scope: QuotaScopeSchema,
    // Human-readable target (e.g. segment name, "Org", or the user's email).
    scopeLabel: z.string(),
    monthlyBudgetUsd: z.number(),
    // USD spent against THIS scope's pool this month (USER = own spend,
    // SEGMENT = all members combined, ORG = everyone combined).
    currentMonthUsedUsd: z.number(),
    remainingUsd: z.number(),
})
    .catchall(z.unknown());
// Response of GET /api/me/quota — fresh (non-ETag-cached) quota standing for the
// signed-in user. `binding` is the tightest (min-remaining) applicable limit and
// drives the client block; `limits` lists every applicable limit for transparency.
export const MeQuotaResponseSchema = z
    .object({
    unlimited: z.boolean(),
    // The user's own monthly spend in USD (always present, even when unlimited).
    currentMonthUsedUsd: z.number(),
    binding: QuotaLimitSchema.nullable(),
    limits: z.array(QuotaLimitSchema),
})
    .catchall(z.unknown());
// ─── GET /api/me/opencode-config ────────────────────────
export const OpenCodeConfigModelEntrySchema = z
    .object({
    name: z.string(),
    contextWindow: z.number().optional(),
    // OpenCode (>=1.16.2) requires BOTH `context` and `output` whenever a
    // `limit` block is present; the generator emits either both or none.
    limit: z
        .object({
        context: z.number().optional(),
        output: z.number().optional(),
    })
        .catchall(z.unknown())
        .optional(),
    // When true, the OpenCode sidecar treats the model as reasoning-capable
    // and synthesizes effort variants (low/medium/high) the desktop surfaces.
    reasoning: z.boolean().optional(),
    // Admin-declared capabilities; when present the sidecar's attachment gate
    // uses this instead of the models.dev catalog lookup.
    modalities: z
        .object({
        input: z.array(z.string()),
        output: z.array(z.string()).optional(),
    })
        .catchall(z.unknown())
        .optional(),
    options: z.record(z.string(), z.unknown()).optional(),
})
    .catchall(z.unknown());
export const OpenCodeConfigProviderEntrySchema = z
    .object({
    // Human-friendly display name shown in the desktop model picker (set for
    // EchoProxy entries whose synthetic key is unreadable).
    name: z.string().optional(),
    // Optional npm package override for non-catalogue provider keys (e.g.
    // `azure-foundry` → `@ai-sdk/openai`).
    npm: z.string().optional(),
    options: z.record(z.string(), z.unknown()),
    models: z.record(z.string(), OpenCodeConfigModelEntrySchema),
})
    .catchall(z.unknown());
export const OpenCodeConfigSkillSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    bundleUrl: z.string(),
    bundleSha256: z.string(),
    bundleVersion: z.string(),
    bundleSizeBytes: z.number(),
    bundleFilename: z.string(),
})
    .catchall(z.unknown());
// Tri-state permission of one MCP tool: 'allow' = Always allow, 'ask' = Needs
// approval, 'deny' = Blocked. A tool absent from `tools` defaults to 'ask'.
export const McpToolStateSchema = z.enum(['allow', 'ask', 'deny']);
export const OpenCodeConfigMcpToolSchema = z
    .object({
    name: z.string(),
    readOnly: z.boolean(),
    state: McpToolStateSchema,
})
    .catchall(z.unknown());
// `headers` carries the per-KB Bearer key for knowledge-base MCP connectors; the
// desktop materializer forwards it to the OpenCode remote MCP `headers` field.
// `tools` is the per-user effective admin tool policy: the per-segment states are
// merged most-permissive-wins across the user's segments bound to the connector.
// The client materializer turns it into OpenCode `permission` keys and may only
// tighten it, never loosen it.
export const OpenCodeConfigMcpSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    headers: z.record(z.string(), z.string()).optional(),
    tools: z.array(OpenCodeConfigMcpToolSchema).optional(),
})
    .catchall(z.unknown());
export const OpenCodeConfigKnowledgeBaseSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    tokenCount: z.number(),
})
    .catchall(z.unknown());
// Global code-execution sandbox config (the desktop code-executor plugin's
// python/node tools POST code here).
export const OpenCodeConfigCodeExecutionSchema = z
    .object({
    url: z.string(),
    token: z.string(),
})
    .catchall(z.unknown());
// EW-48: the organisation's web-tool policy. `blockedUrls` holds match patterns, not URLs —
// the desktop's web-tools-policy plugin is what enforces them.
export const OpenCodeConfigWebToolsSchema = z.object({
    searchEnabled: z.boolean(),
    searchProvider: z.enum(['exa', 'parallel']).nullable(),
    fetchEnabled: z.boolean(),
    blockedUrls: z.array(z.string()),
});
// The response shape BEFORE `.catchall(z.unknown())` is applied below. Kept as its own named
// schema (not inlined) so `OpenCodeConfigResponseFields` — its inferred type — has exact,
// literal `keyof`. Once `.catchall` is applied, the resulting type gets a string index
// signature, which widens `keyof` to `string` and makes `Omit<OpenCodeConfigResponse, K>`
// collapse every kept property to the index signature's `unknown` (Omit is built on
// `Pick<T, Exclude<keyof T, K>>`, and `Exclude<string, K>` is still `string`). Anything that
// needs to omit a field from this response — like the generator's cache-shared subset — must
// build on `OpenCodeConfigResponseFields`, not on `OpenCodeConfigResponse`.
export const OpenCodeConfigResponseFieldsSchema = z.object({
    provider: z.record(z.string(), OpenCodeConfigProviderEntrySchema),
    skills: z.array(OpenCodeConfigSkillSchema),
    mcps: z.array(OpenCodeConfigMcpSchema),
    knowledgeBases: z.array(OpenCodeConfigKnowledgeBaseSchema),
    codeExecution: OpenCodeConfigCodeExecutionSchema.nullable(),
    // Optional: added after clients shipped, and a missing required field costs the whole config.
    webTools: OpenCodeConfigWebToolsSchema.optional(),
    systemPrompt: z.string(),
    echoAdminVersion: z.string(),
    // SHA256 over the settings-defining fields only — volatile usage is
    // excluded. Served as the ETag for If-None-Match polling.
    configHash: z.string(),
    // How often (ms) the desktop should poll opencode-config + quota. Server-controlled
    // (CLIENT_POLL_INTERVAL_MS) and deliberately OUTSIDE configHash: a tuned interval must not
    // flip the ETag and open a reload prompt. Optional — a client without it falls back to its
    // built-in constant (EW-121).
    pollIntervalMs: z.number().int().positive().optional(),
});
export const OpenCodeConfigResponseSchema = OpenCodeConfigResponseFieldsSchema.catchall(z.unknown());
//# sourceMappingURL=me.js.map